/**
 * @format
 */

import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { YesNoModal } from '../../components/common/standardComponents';
import * as routes from '../../routes';
import { processDecisionSupportSubpoints } from '../../actions/decisionSupportActions';
import { startAvailabilitySearch, startPatientSearchProcess, } from '../../actions/rd2RefactorActions';
import GuidedResponseComponent from '../../components/guidedResponse/guidedResponseComponent';
import { DEFAULT_BACK_BUTTON_WARNING_DECISION_SUPPORT_VIEW } from '../../constants/miscStrings';
import PatientInfoTabbedCollapsable from 'features/patient/comps/patientInfoTabbedCollapsable';
import * as types from '../../actions/actionTypes';
import { useAppDispatch, useAppSelector } from "../../store";
import { useAppHistory } from 'hooks';

function DecisionSupportView(props: any) {

	const activePatient: any = useAppSelector((state: any) => state.activePatient);
	const activeCareOrder: any = useAppSelector((state: any) => state.careOrder);
	const auth: any = useAppSelector((state: any) => state.auth);
	const correlationKey: any = useAppSelector((state: any) => state.session.correlationKey);
	const locationEntryPoint: any = useAppSelector((state: any) => state.session.locationEntryPoint);
	const decisionSupportConfig: any = useAppSelector((state: any) => state.config.decisionSupport);
	const isLoading: any = useAppSelector((state: any) => selectIsDecisionSupportLoading(state));
	const patient: any = useAppSelector((state: any) => state.activePatient.details);
	const routePrefix: any = useAppSelector((state: any) => state.config.instance.routePrefix);
	const appointmentFirstMode: boolean = useAppSelector((state: any) => state.session.appointmentFirstMode);

	const [isBackButtonModalOpen, setIsBackButtonModalOpen] = useState<boolean>(false);

	const dispatch = useAppDispatch();
	const history = useAppHistory();

	const setBrowserHistoryState = () => {
		window.history.pushState(null, '', window.location.pathname);
	};

	const createBackButtonModalEventListener = () => {
		window.addEventListener('popstate', onBrowserBackButtonClick);
	};

	useEffect(() => {
		setBrowserHistoryState();
		createBackButtonModalEventListener();
	}, []);

	const removeBackButtonModalEventListener = () => {
		window.removeEventListener('popstate', onBrowserBackButtonClick);
	};

	useEffect(() => {
		return () => {
			removeBackButtonModalEventListener();
		}
	}, []);

	const onBrowserBackButtonClick = (e: any) => {
		e.preventDefault();
		setIsBackButtonModalOpen(true);
	};

	const handleGuidedResponseFlowComplete = (results: any) => {
		dispatch(processDecisionSupportSubpoints(results, true)).then((data: any) => {
			dispatch(
				startAvailabilitySearch(
					data.activeCareOrder,
					data.decisionSupportOutput,
					data.availabilitySearchConfig,
					data.patientDetails,
					data.routePrefix,
				),
			);
		});
	};

	const buildGuidedResponseInputData = (patient: any, activeCareOrderDetails: any) => {
		var fields: any[] = [];

		mapPatientToGuidedResponseInputFields(fields, patient);
		mapCareOrderToGuidedResponseInputFields(fields, activeCareOrderDetails);
		mapLocationEntryPointToGuidedResponseInputFields(fields, locationEntryPoint);

		return { inputFields: fields };
	};

	const mapLocationEntryPointToGuidedResponseInputFields = (fields: any[], entryPoint: any) => {
		if (!entryPoint?.siteId) return;

		// Launch SiteId is authoritative for location deep-links; care-order SiteId may also be present.
		addOrOverrideFieldIfPresent(fields, 'SiteId', 'String', String(entryPoint.siteId));
	};

	const mapPatientToGuidedResponseInputFields = (fields: any[], patient: any) => {
		if (patient) {
			addOrOverrideFieldIfPresent(fields, 'PatientReferenceId', 'String', patient.referenceId);
			addOrOverrideFieldIfPresent(fields, 'PatientMemberId', 'String', patient.memberId);
			addOrOverrideFieldIfPresent(fields, 'PatientGroupNumber', 'String', patient.groupNumber);
			addOrOverrideFieldIfPresent(fields, 'PatientFirstName', 'String', patient.firstName);
			addOrOverrideFieldIfPresent(fields, 'PatientMiddleName', 'String', patient.middleName);
			addOrOverrideFieldIfPresent(fields, 'PatientLastName', 'String', patient.lastName);
			addOrOverrideFieldIfPresent(fields, 'PatientDateOfBirth', 'Date', getDateString(patient.dateOfBirth));
			addOrOverrideFieldIfPresent(fields, 'PatientGenderCode', 'String', patient.gender);
			addOrOverrideFieldIfPresent(fields, 'PatientGuardianName', 'String', patient.guardianName);
			addOrOverrideFieldIfPresent(fields, 'PatientEmail', 'String', patient.email);
			addOrOverrideFieldIfPresent(fields, 'PatientHomePhone', 'String', patient.homePhone);
			addOrOverrideFieldIfPresent(fields, 'PatientMobilePhone', 'String', patient.mobilePhone);
			addOrOverrideFieldIfPresent(fields, 'PatientAlternatePhone', 'String', patient.alternatePhone);
			addOrOverrideFieldIfPresent(fields, 'PatientAddressLine1', 'String', patient.addressLine1);
			addOrOverrideFieldIfPresent(fields, 'PatientAddressLine2', 'String', patient.addressLine2);
			addOrOverrideFieldIfPresent(fields, 'PatientCityName', 'String', patient.cityName);
			addOrOverrideFieldIfPresent(fields, 'PatientStateCode', 'String', patient.stateCode);
			addOrOverrideFieldIfPresent(fields, 'PatientZipCode', 'String', patient.zipCode);
			addOrOverrideFieldIfPresent(fields, 'PatientLatitude', 'String', patient.latitude);
			addOrOverrideFieldIfPresent(fields, 'PatientLongitude', 'String', patient.longitude);
			addOrOverrideFieldIfPresent(fields, 'PatientPoolPrefix', 'String', patient.patientPoolPrefix);
			addOrOverrideFieldIfPresent(fields, 'PatientPoolUniqueToken', 'String', patient.patientPool?.uniqueToken);
		}
	};

	const mapCareOrderToGuidedResponseInputFields = (fields: any[], activeCareOrderDetails: any) => {
		if (activeCareOrderDetails) {
			if (!fields.find((f) => f.fieldName === 'PatientZipCode')) {
				addOrOverrideFieldIfPresent(fields, 'PatientZipCode', 'String', activeCareOrderDetails.patientZip);
			}

			addOrOverrideFieldIfPresent(
				fields,
				'MaxVisitDate',
				'Date',
				getDateString(activeCareOrderDetails.maxVisitDate),
			);
			addOrOverrideFieldIfPresent(
				fields,
				'MinVisitDate',
				'Date',
				getDateString(activeCareOrderDetails.minVisitDate),
			);
			addOrOverrideFieldIfPresent(
				fields,
				'OrderExpirationDate',
				'Date',
				getDateString(activeCareOrderDetails.expirationDate),
			);

			if (activeCareOrderDetails.appointments && activeCareOrderDetails.appointments.length === 1) {
				let appointment = activeCareOrderDetails.appointments[0];

				addOrOverrideFieldIfPresent(fields, 'AppointmentTypeId', 'String', appointment.appointmentTypeId);
				addOrOverrideFieldIfPresent(fields, 'AppointmentTypeName', 'String', appointment.appointmentTypeName);

				//currently NPI isn't passed via careorders/appt BUT if it ever is then we have a hook for it.
				addOrOverrideFieldIfPresent(fields, 'NPI', 'String', appointment.npi);

				addOrOverrideFieldIfPresent(fields, 'ServiceTypeId', 'String', appointment.serviceTypeId);
				addOrOverrideFieldIfPresent(fields, 'ServiceId', 'String', appointment.serviceId);
				addOrOverrideFieldIfPresent(fields, 'SpecialtyId', 'String', appointment.specialtyId);
				addOrOverrideFieldIfPresent(fields, 'SiteId', 'String', appointment.siteId);
				addOrOverrideFieldIfPresent(
					fields,
					'ExternalReferralOrderNumber',
					'String',
					appointment.externalReferralOrderId,
				);

				if (appointment.careOrderCustomField !== null && appointment.careOrderCustomField.length) {
					appointment.careOrderCustomField.forEach((x: any) => {
						addOrOverrideFieldIfPresent(fields, `${x.fieldName}`, 'String', `${x.fieldValue}`);
					});
				}
			}
		}
	};

	const getDateString = (date: string | Date) => {
		if (date !== null && date !== undefined) {
			const d = moment(date).format('YYYY-MM-DD');
			return d;
		}
	};

	//TODO: move addFieldIfPresent and createField to a GuidedResponseHelpers file?
	const addOrOverrideFieldIfPresent = (fields: any[], fieldName: string, valueType: string, currentValue: any) => {
		// Note: The "if present" refers the presence of a currentValue to assign, not a field in the list
		if (currentValue) {
			let field = fields.find((f) => f.fieldName.toLowerCase() === fieldName.toLowerCase());
			if (field === undefined) {
				field = createField(fieldName, valueType, currentValue);
				fields.push(field);
			} else {
				// Code assumes value types agree, else we have a bug
				field.currentValue = currentValue;
			}
		}
	};

	const createField = (fieldName: string, valueType: string, currentValue: any) => {
		var field = {
			fieldName: fieldName,
			valueType: valueType,
			currentValue: currentValue,
		};
		return field;
	};

	const redirectToPatientSearch = () => {
		dispatch(startPatientSearchProcess(routePrefix));
	};

	// force redirect to patient search if no patient in context (patient-first only)
	if (!appointmentFirstMode && (!patient.referenceId || patient.referenceId === '')) {
		redirectToPatientSearch();
		return null;
	}

	// force redirect to availability search if decision support is not enabled
	if (!decisionSupportConfig.useDecisionSupport) {
		history.navigate(routes.availabilitySearchUrl(routePrefix));
		return null;
	}

	const inputData = buildGuidedResponseInputData(appointmentFirstMode ? null : patient, activeCareOrder.activeCareOrderDetails);

	let showPatientHeader = !appointmentFirstMode && activePatient.details?.referenceId !== '' ? true : false;

	return (
		<>
			<div>
				<YesNoModal
					isOpen={isBackButtonModalOpen}
					body={DEFAULT_BACK_BUTTON_WARNING_DECISION_SUPPORT_VIEW}
					noLabel="No"
					yesLabel="Yes"
					title="Are you sure you want to go back?"
					onNo={() => {
						setBrowserHistoryState();
						setIsBackButtonModalOpen(false);
					}}
					onYes={() => {
						setIsBackButtonModalOpen(false);
						redirectToPatientSearch();
					}}
				/>
			</div>
			{showPatientHeader ? (
				<div>
					<div className="row">
						<div className="col">
							<PatientInfoTabbedCollapsable />
						</div>
					</div>
				</div>
			) : (
				''
			)}
			<GuidedResponseComponent
				contextData={inputData}
				correlationKey={correlationKey}
				isLoading={isLoading}
				onCompleted={handleGuidedResponseFlowComplete}
				productInstanceId={auth.productInstanceId}
				patientReferenceId={appointmentFirstMode ? null : patient.referenceId}
				careOrderVisitIdentifier={activeCareOrder.activeCareOrderDetails.careOrderVisitIdentifier}
			/>
		</>
	);
}

const selectIsDecisionSupportLoading = (state: any) => {
	return state.ajaxStatus.isLoading.some((type: string) => [
		types.GUIDED_RESPONSE_START_FLOW,
		types.GUIDED_RESPONSE_CONTINUE_FLOW,
		types.GUIDED_RESPONSE_PREVIOUS,
	].includes(type))
}

export default DecisionSupportView;
